import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import AdvancedFilters from '../../components/admin/AdvancedFilters';
import BulkActions from '../../components/admin/BulkActions';
import BulkConfirmModal from '../../components/admin/BulkConfirmModal';
import api, { getAll } from '../../services/api';
import authStore from '../../store/authStore';
import { puoModificare } from '../../utils/permessi';
import { notify } from '../../utils/notifications';
import {
  Plus, Search, Eye, Edit, Trash2, FileText, Calendar, User, X, 
  Pause, Play, Download
} from 'lucide-react';

const Abbonamenti = () => {
  const { utente } = authStore();
  const modificabile = puoModificare(utente);
  const navigate = useNavigate();
  const [abbonamenti, setAbbonamenti] = useState([]);
  const [filteredAbbonamenti, setFilteredAbbonamenti] = useState([]);
  const [titolari, setTitolari] = useState([]);
  const [linee, setLinee] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAbbonamento, setEditingAbbonamento] = useState(null);
  
  // Bulk selection states
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkModalConfig, setBulkModalConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    actionLabel: '',
    actionColor: 'blue',
    onConfirm: null
  });
  
  const [tipiAbbonamento, setTipiAbbonamento] = useState([]);
  // Località del gruppo selezionato: salita all'andata, discesa al ritorno
  const [localita, setLocalita] = useState([]);

  // Primo anno scolastico non ancora concluso: quello iniziato a settembre
  // di Y finisce il 30 giugno di Y+1, quindi da luglio in poi non va più
  // proposto.
  const annoScolasticoCorrente = (() => {
    const oggi = new Date();
    return oggi.getMonth() + 1 > 6 ? oggi.getFullYear() : oggi.getFullYear() - 1;
  })();

  const [formData, setFormData] = useState({
    titolare: '',
    linea: '',
    tipo_abbonamento: '',
    direzione: 'ANDATA_RITORNO',
    localita_andata: '',
    localita_ritorno: '',
    anno_scolastico: annoScolasticoCorrente,
    quota_mensile_personalizzata: '',
    importo_settembre: '',
    data_inizio: '',
    data_fine: '',
    importo: '',
    note: ''
  });

  // Tipo selezionato e relativa modalità: guidano quali campi mostrare
  const tipoSelezionato = tipiAbbonamento.find(
    (t) => String(t.id) === String(formData.tipo_abbonamento)
  );
  const modalitaSelezionata = tipoSelezionato?.modalita || null;
  const isScuole = modalitaSelezionata === 'SCUOLE';

  // Località del gruppo scelto
  const localitaDisponibili = localita.filter(
    (l) => String(l.gruppo) === String(formData.linea)
  );

  // Tipi disponibili per la linea scelta
  const tipiDisponibili = tipiAbbonamento.filter(
    (t) => t.attivo && String(t.linea) === String(formData.linea)
  );

  // Prezzo proposto dal tipo di abbonamento: è solo un punto di partenza,
  // l'operatore può sovrascriverlo su questo abbonamento.
  const quotaDaListino = tipoSelezionato
    ? (formData.direzione === 'ANDATA_RITORNO'
        ? Number(tipoSelezionato.importo)
        : Number(tipoSelezionato.importo_sola_direzione ?? tipoSelezionato.importo))
    : 0;

  // Quando cambia tipo o direzione si ripropongono i prezzi di listino
  useEffect(() => {
    if (!tipoSelezionato || tipoSelezionato.modalita !== 'SCUOLE') return;
    setFormData((f) => ({
      ...f,
      quota_mensile_personalizzata: quotaDaListino.toFixed(2),
      importo_settembre: (
        tipoSelezionato.importo_settembre !== null && tipoSelezionato.importo_settembre !== undefined
          ? Number(tipoSelezionato.importo_settembre)
          : quotaDaListino / 2
      ).toFixed(2),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.tipo_abbonamento, formData.direzione]);

  const quotaMensile = Number(formData.quota_mensile_personalizzata) || 0;
  const quotaSettembre = Number(formData.importo_settembre) || 0;
  // 8 mensilità piene (ottobre-maggio) + settembre; giugno è gratuito
  const totaleAnnuo = isScuole ? (quotaSettembre + quotaMensile * 8).toFixed(2) : null;

  useEffect(() => {
    caricaDati();
  }, []);

  const caricaDati = async () => {
    try {
      setLoading(true);
      
      // Carica abbonamenti
      const abbData = await getAll('/abbonamenti/abbonamenti/');
      setAbbonamenti(abbData);
      setFilteredAbbonamenti(abbData);

      // Carica titolari (clienti) per il form
      const titolariData = await getAll('/anagrafiche/titolari/');
      setTitolari(titolariData);
      
      console.log('Titolari (clienti) caricati:', titolariData.length); // Debug

      // Carica linee per il form
      setLinee(await getAll('/trasporti/linee/'));

      // Carica i tipi di abbonamento configurati sui gruppi
      setTipiAbbonamento(await getAll('/abbonamenti/tipi-abbonamento/'));

      // Carica le località di tutti i gruppi
      setLocalita(await getAll('/trasporti/localita/', { attiva: true }));

    } catch (error) {
      console.error('Errore caricamento dati:', error);
      notify.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = (filterValues) => {
    let filtered = [...abbonamenti];

    // Filtro per ricerca testuale
    if (filterValues.search) {
      const searchLower = filterValues.search.toLowerCase();
      filtered = filtered.filter(abb =>
        abb.codice?.toLowerCase().includes(searchLower) ||
        abb.titolare_nome?.toLowerCase().includes(searchLower) ||
        (abb.tratta || abb.linea_nome)?.toLowerCase().includes(searchLower)
      );
    }

    // Filtro multi-stato
    if (filterValues.stati && filterValues.stati.length > 0) {
      filtered = filtered.filter(abb => filterValues.stati.includes(abb.stato));
    }

    // Filtro per linea
    if (filterValues.linea) {
      filtered = filtered.filter(abb => abb.linea === parseInt(filterValues.linea));
    }

    // Filtro range date inizio
    if (filterValues.data_inizio_inizio) {
      filtered = filtered.filter(abb => abb.data_inizio >= filterValues.data_inizio_inizio);
    }
    if (filterValues.data_inizio_fine) {
      filtered = filtered.filter(abb => abb.data_inizio <= filterValues.data_inizio_fine);
    }

    // Filtro range date fine
    if (filterValues.data_fine_inizio) {
      filtered = filtered.filter(abb => abb.data_fine >= filterValues.data_fine_inizio);
    }
    if (filterValues.data_fine_fine) {
      filtered = filtered.filter(abb => abb.data_fine <= filterValues.data_fine_fine);
    }

    // Filtro range prezzo
    if (filterValues.prezzo_min) {
      filtered = filtered.filter(abb => parseFloat(abb.prezzo) >= parseFloat(filterValues.prezzo_min));
    }
    if (filterValues.prezzo_max) {
      filtered = filtered.filter(abb => parseFloat(abb.prezzo) <= parseFloat(filterValues.prezzo_max));
    }

    setFilteredAbbonamenti(filtered);
  };

  // Bulk selection handlers
  const handleSelectAll = () => {
    if (selectedIds.length === filteredAbbonamenti.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredAbbonamenti.map(abb => abb.id));
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id]
    );
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  // Bulk actions
  const handleBulkSuspend = () => {
    const selectedItems = filteredAbbonamenti.filter(abb => selectedIds.includes(abb.id));
    setBulkModalConfig({
      isOpen: true,
      title: 'Sospendi Abbonamenti',
      message: 'Sei sicuro di voler sospendere i seguenti abbonamenti?',
      actionLabel: 'Sospendi',
      actionColor: 'orange',
      onConfirm: async () => {
        return await executeBulkAction(selectedIds, 'sospendi');
      },
      items: selectedItems.map(abb => ({ id: abb.id, name: abb.codice || abb.id })),
      itemLabel: 'abbonamento',
      warningText: 'Gli abbonamenti sospesi non saranno più validi fino alla riattivazione.'
    });
  };

  const handleBulkActivate = () => {
    const selectedItems = filteredAbbonamenti.filter(abb => selectedIds.includes(abb.id));
    setBulkModalConfig({
      isOpen: true,
      title: 'Attiva Abbonamenti',
      message: 'Sei sicuro di voler attivare i seguenti abbonamenti?',
      actionLabel: 'Attiva',
      actionColor: 'green',
      onConfirm: async () => {
        return await executeBulkAction(selectedIds, 'attiva');
      },
      items: selectedItems.map(abb => ({ id: abb.id, name: abb.codice || abb.id })),
      itemLabel: 'abbonamento'
    });
  };

  const handleBulkExport = () => {
    const selectedItems = filteredAbbonamenti.filter(abb => selectedIds.includes(abb.id));
    exportToCSV(selectedItems);
    notify.success(`${selectedItems.length} abbonamenti esportati in CSV`);
    setSelectedIds([]);
  };

  const executeBulkAction = async (ids, action) => {
    const results = {
      success: [],
      failed: []
    };

    for (const id of ids) {
      try {
        await api.post(`/abbonamenti/abbonamenti/${id}/${action}/`);
        results.success.push(id);
      } catch (error) {
        const abbonamento = filteredAbbonamenti.find(a => a.id === id);
        results.failed.push({
          id,
          name: abbonamento?.codice || id,
          error: error.response?.data?.errore || 'Errore sconosciuto'
        });
      }
    }

    // Ricarica dati se almeno una operazione è riuscita
    if (results.success.length > 0) {
      await caricaDati();
      setSelectedIds([]);
    }

    return results;
  };

  const exportToCSV = (data) => {
    const headers = ['Codice', 'Titolare', 'Linea', 'Stato', 'Data Inizio', 'Data Fine', 'Prezzo'];
    const rows = data.map(abb => [
      abb.codice || '',
      abb.titolare_nome || '',
      abb.linea_nome || '',
      abb.stato || '',
      abb.data_inizio || '',
      abb.data_fine || '',
      abb.prezzo || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `abbonamenti_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const bulkActions = [
    {
      id: 'suspend',
      label: 'Sospendi',
      icon: Pause,
      color: 'orange',
      onClick: handleBulkSuspend
    },
    {
      id: 'activate',
      label: 'Attiva',
      icon: Play,
      color: 'green',
      onClick: handleBulkActivate
    },
    {
      id: 'export',
      label: 'Esporta CSV',
      icon: Download,
      color: 'blue',
      onClick: handleBulkExport
    }
  ];

  const filterConfig = [
    {
      id: 'search',
      label: 'Cerca',
      type: 'text',
      placeholder: 'Codice, cliente, linea...'
    },
    {
      id: 'stati',
      label: 'Stati',
      type: 'multi-select',
      options: [
        { value: 'ATTIVO', label: 'Attivo' },
        { value: 'SCADUTO', label: 'Scaduto' },
        { value: 'SOSPESO', label: 'Sospeso' },
        { value: 'IN_ATTESA_PAGAMENTO', label: 'In Attesa Pagamento' }
      ]
    },
    {
      id: 'linea',
      label: 'Linea',
      type: 'select',
      options: linee.map(l => ({ value: l.id.toString(), label: l.nome }))
    },
    {
      id: 'data_inizio',
      label: 'Data Inizio',
      type: 'date-range'
    },
    {
      id: 'data_fine',
      label: 'Data Scadenza',
      type: 'date-range'
    },
    {
      id: 'prezzo',
      label: 'Prezzo (€)',
      type: 'number-range',
      step: 0.01
    }
  ];

  const handleOpenModal = (abbonamento = null) => {
    if (abbonamento) {
      setEditingAbbonamento(abbonamento);
      setFormData({
        titolare: abbonamento.titolare,
        linea: abbonamento.linea,
        tipo_abbonamento: abbonamento.tipo_abbonamento || '',
        direzione: abbonamento.direzione || 'ANDATA_RITORNO',
        localita_andata: abbonamento.localita_andata || '',
        localita_ritorno: abbonamento.localita_ritorno || '',
        anno_scolastico: abbonamento.anno_scolastico || annoScolasticoCorrente,
        quota_mensile_personalizzata: abbonamento.quota_mensile_applicata ?? '',
        importo_settembre: abbonamento.quota_settembre_applicata ?? '',
        data_inizio: abbonamento.data_inizio,
        data_fine: abbonamento.data_fine,
        importo: abbonamento.importo,
        note: abbonamento.note || ''
      });
    } else {
      setEditingAbbonamento(null);
      setFormData({
        titolare: '',
        linea: '',
        tipo_abbonamento: '',
        direzione: 'ANDATA_RITORNO',
        localita_andata: '',
        localita_ritorno: '',
        anno_scolastico: annoScolasticoCorrente,
        quota_mensile_personalizzata: '',
        importo_settembre: '',
        data_inizio: '',
        data_fine: '',
        importo: '',
        note: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAbbonamento(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const toastId = notify.loading(editingAbbonamento ? 'Aggiornamento...' : 'Creazione...');

    // Negli abbonamenti scolastici date e importo li calcola il server
    // a partire dall'anno scolastico: inviarli a mano sarebbe fuorviante.
    const payload = {
      titolare: formData.titolare,
      linea: formData.linea,
      tipo_abbonamento: formData.tipo_abbonamento,
      direzione: formData.direzione,
      localita_andata: formData.localita_andata || null,
      localita_ritorno: formData.localita_ritorno || null,
      note: formData.note,
    };
    if (isScuole) {
      payload.anno_scolastico = formData.anno_scolastico;
      payload.quota_mensile_personalizzata = formData.quota_mensile_personalizzata;
      payload.importo_settembre = formData.importo_settembre;
    } else {
      payload.data_inizio = formData.data_inizio;
      payload.data_fine = formData.data_fine;
      payload.importo = formData.importo;
    }

    try {
      if (editingAbbonamento) {
        await api.put(`/abbonamenti/abbonamenti/${editingAbbonamento.id}/`, payload);
        notify.update(toastId, { type: 'success', render: 'Abbonamento aggiornato con successo!' });
      } else {
        await api.post('/abbonamenti/abbonamenti/', payload);
        notify.update(toastId, { type: 'success', render: 'Abbonamento creato con successo!' });
      }
      await caricaDati();
      handleCloseModal();
    } catch (error) {
      console.error('Errore salvataggio abbonamento:', error);
      // Riporta il primo messaggio utile, da qualunque campo provenga
      const dati = error.response?.data || {};
      const primoErroreCampo = Object.values(dati)
        .flat()
        .find((v) => typeof v === 'string');
      const errorMsg = dati.detail || primoErroreCampo || 'Errore nel salvataggio';
      notify.update(toastId, { type: 'error', render: errorMsg });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo abbonamento?')) return;
    
    const toastId = notify.loading('Eliminazione...');
    try {
      await api.delete(`/abbonamenti/abbonamenti/${id}/`);
      await caricaDati();
      notify.update(toastId, { type: 'success', render: 'Abbonamento eliminato con successo!' });
    } catch (error) {
      console.error('Errore eliminazione abbonamento:', error);
      notify.update(toastId, { type: 'error', render: 'Errore nell\'eliminazione dell\'abbonamento' });
    }
  };

  const getStatoBadge = (stato) => {
    const styles = {
      'ATTIVO': 'bg-green-100 text-green-800',
      'SCADUTO': 'bg-red-100 text-red-800',
      'SOSPESO': 'bg-yellow-100 text-yellow-800',
      'IN_ATTESA_PAGAMENTO': 'bg-orange-100 text-orange-800'
    };
    return styles[stato] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6">
        {/* Header compatto */}
        <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Abbonamenti</h1>
            <p className="text-sm text-gray-500 mt-0.5">Gestione abbonamenti</p>
          </div>
          {modificabile && (
          <button
            onClick={() => handleOpenModal()}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Nuovo Abbonamento
          </button>
          )}
        </div>

        {/* Advanced Filters */}
        <AdvancedFilters
          filters={filterConfig}
          onApply={handleApplyFilters}
          resultCount={filteredAbbonamenti.length}
        />

        {/* Bulk Actions */}
        <BulkActions
          selectedIds={selectedIds}
          totalCount={filteredAbbonamenti.length}
          onSelectAll={handleSelectAll}
          onClearSelection={handleClearSelection}
          actions={bulkActions}
          allSelected={selectedIds.length === filteredAbbonamenti.length && filteredAbbonamenti.length > 0}
        />

        {/* Tabella Abbonamenti */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          {filteredAbbonamenti.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Nessun abbonamento trovato</h3>
              <p className="text-sm text-gray-500 mb-4">
                {filteredAbbonamenti.length === 0 && abbonamenti.length > 0
                  ? 'Prova con filtri diversi'
                  : 'Inizia creando il primo abbonamento'}
              </p>
              {filteredAbbonamenti.length === 0 && abbonamenti.length === 0 && (
                <button onClick={() => handleOpenModal()} className="btn-primary inline-flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Crea Abbonamento
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table-compact">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.length === filteredAbbonamenti.length && filteredAbbonamenti.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4 cursor-pointer accent-blue-600"
                      />
                    </th>
                    <th>Abbonamento</th>
                    <th>Cliente</th>
                    <th>Linea</th>
                    <th>Validità</th>
                    <th>Importo</th>
                    <th>Stato</th>
                    <th className="text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAbbonamenti.map((abb) => (
                    <tr key={abb.id} className={selectedIds.includes(abb.id) ? 'bg-blue-50' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(abb.id)}
                          onChange={() => handleSelectOne(abb.id)}
                          className="w-4 h-4 cursor-pointer accent-blue-600"
                        />
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-[#1b94ce]" />
                          <div>
                            <div className="text-sm font-semibold text-gray-900">{abb.codice}</div>
                            <div className="text-xs text-gray-500">
                              {abb.tipo_abbonamento_nome || 'Standard'}
                              {abb.direzione_display && ` · ${abb.direzione_display}`}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="text-sm">{abb.titolare_nome || 'N/A'}</span>
                        </div>
                      </td>
                      <td>
                        <span className="text-sm text-gray-600">{abb.tratta || abb.linea_nome || '—'}</span>
                      </td>
                      <td>
                        <div className="flex items-start gap-1.5 text-xs text-gray-600">
                          <Calendar className="w-3.5 h-3.5 text-gray-400 mt-0.5" />
                          <div>
                            <div>{new Date(abb.data_inizio).toLocaleDateString('it-IT')}</div>
                            <div className="text-gray-500">{new Date(abb.data_fine).toLocaleDateString('it-IT')}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="text-sm font-semibold">€ {parseFloat(abb.importo).toFixed(2)}</span>
                      </td>
                      <td>
                        <span className={`badge ${getStatoBadge(abb.stato)}`}>{abb.stato}</span>
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => navigate(`/admin/abbonamenti/${abb.id}`)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition" title="Dettagli">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleOpenModal(abb)} className="p-1.5 text-green-600 hover:bg-green-50 rounded transition" title="Modifica">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(abb.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded transition" title="Elimina">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {filteredAbbonamenti.length > 0 && (
          <div className="mt-3 text-xs text-gray-500 text-right">
            {filteredAbbonamenti.length} {filteredAbbonamenti.length === 1 ? 'abbonamento' : 'abbonamenti'}
          </div>
        )}
      </div>

      {/* Modal Creazione/Modifica - Compatto */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">
                {editingAbbonamento ? 'Modifica Abbonamento' : 'Nuovo Abbonamento'}
              </h2>
              <button onClick={handleCloseModal} className="p-1.5 hover:bg-gray-100 rounded transition">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Cliente *</label>
                <select
                  required
                  value={formData.titolare}
                  onChange={(e) => setFormData({ ...formData, titolare: e.target.value })}
                  className="input-compact"
                >
                  <option value="">Seleziona cliente</option>
                  {titolari.map((tit) => (
                    <option key={tit.id} value={tit.id}>{tit.nome_completo} - {tit.codice_fiscale}</option>
                  ))}
                </select>
                {titolari.length === 0 && (
                  <p className="text-xs text-orange-600 mt-1">⚠️ Nessun cliente disponibile</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Gruppo *</label>
                <select
                  required
                  value={formData.linea}
                  onChange={(e) => setFormData({ ...formData, linea: e.target.value, tipo_abbonamento: '' })}
                  className="input-compact"
                >
                  <option value="">Seleziona gruppo</option>
                  {linee.map((linea) => (
                    <option key={linea.id} value={linea.id}>{linea.codice} - {linea.nome}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Tipo di abbonamento *</label>
                <select
                  required
                  disabled={!formData.linea}
                  value={formData.tipo_abbonamento}
                  onChange={(e) => setFormData({ ...formData, tipo_abbonamento: e.target.value })}
                  className="input-compact disabled:bg-gray-100"
                >
                  <option value="">
                    {formData.linea ? 'Seleziona tipo' : 'Seleziona prima il gruppo'}
                  </option>
                  {tipiDisponibili.map((tipo) => (
                    <option key={tipo.id} value={tipo.id}>
                      {tipo.nome} — {tipo.modalita === 'SCUOLE' ? `€${tipo.importo}/mese` : `€${tipo.importo}`}
                    </option>
                  ))}
                </select>
                {formData.linea && tipiDisponibili.length === 0 && (
                  <p className="text-xs text-orange-600 mt-1">
                    ⚠️ Nessun tipo di abbonamento configurato per questo gruppo
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Direzione *</label>
                <select
                  required
                  value={formData.direzione}
                  onChange={(e) => setFormData({ ...formData, direzione: e.target.value })}
                  className="input-compact"
                >
                  <option value="ANDATA_RITORNO">Andata e ritorno</option>
                  <option value="SOLO_ANDATA">Solo andata</option>
                  <option value="SOLO_RITORNO">Solo ritorno</option>
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Località salita (andata)
                  </label>
                  <select
                    disabled={!formData.linea || formData.direzione === 'SOLO_RITORNO'}
                    value={formData.localita_andata}
                    onChange={(e) => setFormData({ ...formData, localita_andata: e.target.value })}
                    className="input-compact disabled:bg-gray-100"
                  >
                    <option value="">Nessuna</option>
                    {localitaDisponibili.map((l) => (
                      <option key={l.id} value={l.id}>{l.nome}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Località discesa (ritorno)
                  </label>
                  <select
                    disabled={!formData.linea || formData.direzione === 'SOLO_ANDATA'}
                    value={formData.localita_ritorno}
                    onChange={(e) => setFormData({ ...formData, localita_ritorno: e.target.value })}
                    className="input-compact disabled:bg-gray-100"
                  >
                    <option value="">Nessuna</option>
                    {localitaDisponibili.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.nome}{l.orario_ritorno ? ` — rientro ${l.orario_ritorno.slice(0,5)}` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-gray-500 mt-1">Determina l'orario di rientro</p>
                </div>
              </div>

              {isScuole ? (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Anno scolastico *</label>
                    <select
                      required
                      value={formData.anno_scolastico}
                      onChange={(e) => setFormData({ ...formData, anno_scolastico: e.target.value })}
                      className="input-compact"
                    >
                      {[annoScolasticoCorrente, annoScolasticoCorrente + 1].map((anno) => (
                        <option key={anno} value={anno}>{anno}/{anno + 1}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Quota mensile (€) *
                      </label>
                      <input
                        type="number"
                        required
                        min="0"
                        step="0.01"
                        value={formData.quota_mensile_personalizzata}
                        onChange={(e) => setFormData({ ...formData, quota_mensile_personalizzata: e.target.value })}
                        className="input-compact"
                      />
                      <p className="text-[11px] text-gray-500 mt-1">
                        Listino: €{quotaDaListino.toFixed(2)}
                        {formData.direzione !== 'ANDATA_RITORNO' && ' (sola direzione)'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Quota settembre (€) *
                      </label>
                      <input
                        type="number"
                        required
                        min="0"
                        step="0.01"
                        value={formData.importo_settembre}
                        onChange={(e) => setFormData({ ...formData, importo_settembre: e.target.value })}
                        className="input-compact"
                      />
                      <p className="text-[11px] text-gray-500 mt-1">
                        Mezzo mese (15-30 settembre)
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1.5">
                    <p className="text-xs font-bold text-gray-900">Riepilogo abbonamento scolastico</p>
                    <p className="text-xs text-gray-600">
                      Periodo: <strong>15 settembre {formData.anno_scolastico} → 30 giugno {Number(formData.anno_scolastico) + 1}</strong>
                    </p>
                    <p className="text-xs text-gray-600">
                      Settembre €{quotaSettembre.toFixed(2)} · ottobre-maggio €{quotaMensile.toFixed(2)} al mese · giugno gratuito
                    </p>
                    <p className="text-xs text-gray-900 pt-1 border-t border-gray-200">
                      Totale anno: <strong>€{totaleAnnuo}</strong> in 10 mensilità, di cui 9 da incassare.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Data Inizio *</label>
                      <input type="date" required value={formData.data_inizio} onChange={(e) => setFormData({ ...formData, data_inizio: e.target.value })} className="input-compact" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Data Fine *</label>
                      <input type="date" required value={formData.data_fine} onChange={(e) => setFormData({ ...formData, data_fine: e.target.value })} className="input-compact" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Importo (€) *</label>
                    <input type="number" step="0.01" required value={formData.importo} onChange={(e) => setFormData({ ...formData, importo: e.target.value })} className="input-compact" placeholder="0.00" />
                  </div>
                </>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Note</label>
                <textarea value={formData.note} onChange={(e) => setFormData({ ...formData, note: e.target.value })} rows={3} className="input-compact" />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
                <button type="button" onClick={handleCloseModal} className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium">
                  Annulla
                </button>
                <button type="submit" className="btn-primary">
                  {editingAbbonamento ? 'Salva' : 'Crea'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Confirm Modal */}
      <BulkConfirmModal
        isOpen={bulkModalConfig.isOpen}
        onClose={() => setBulkModalConfig({ ...bulkModalConfig, isOpen: false })}
        onConfirm={bulkModalConfig.onConfirm}
        title={bulkModalConfig.title}
        message={bulkModalConfig.message}
        actionLabel={bulkModalConfig.actionLabel}
        actionColor={bulkModalConfig.actionColor}
        selectedCount={selectedIds.length}
        items={bulkModalConfig.items || []}
        itemLabel={bulkModalConfig.itemLabel || 'elemento'}
        warningText={bulkModalConfig.warningText}
      />
    </AdminLayout>
  );
};

export default Abbonamenti;
